import { Component, ElementRef, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { creFadeInOut } from 'cre-ng';
import { MenuItem, MessageService, SelectItem } from 'primeng/api';

import { User } from '@app/core/core.model';
import {
  CommitmentChoiceDto,
  FleetWeeklyDto,
  RecordingsDto,
  Tractor,
} from '@app/core/driver-wig.model';
import { DayDetail, IDayDetail } from '@app/core/model/qualifications.model';
import { IBonusQualificationService } from '@app/core/services/interface/IBonusQualificationService';
import { IDriverWigService } from '@app/core/services/interface/IDriverWigService';
import { ServiceFactory } from '@app/core/services/service.factory';
import { CommitmentStatus, DriverCommitmentConfiguration } from '@app/shared/shared.models';
import { AuthService } from '@app/core/auth.service';
import { IDriverVOTService } from '@app/core/services/interface/IDriverVOTService';
import { ActivatedRoute } from '@angular/router';
import { WigSummaryService } from './wig-summary.service';

@Component({
  animations: [creFadeInOut],
  selector: 'app-wig-summary',
  styleUrls: ['./wig-summary.component.css'],
  templateUrl: './wig-summary.component.html',
})
export class WigSummaryComponent implements OnInit {
  @Output() driverType = new EventEmitter<string>();
  @Output() fleet = new EventEmitter<string>();
  @Output() refreshStats = new EventEmitter<string>();
  @ViewChild('recordingPlayer') recordingPlayer: ElementRef<HTMLVideoElement>;
  bonusQualificationService: IBonusQualificationService;
  commitmentChoices: CommitmentChoiceDto[];
  commitmentDetails: DayDetail[];
  commitmentSaveMessage: string;
  configurationDescription: string;
  configurationDetails: DriverCommitmentConfiguration[];
  configurationHeader: string;
  currentCommitmentMenuChoices: SelectItem[];
  daily5Commitment = 'Daily 5';
  driverWigService: IDriverWigService;
  driverVOTService: IDriverVOTService;
  fleetWeekly: FleetWeeklyDto;
  isCommitmentFormVisible = false;
  isCommitmentMenuVisible = false;
  isConfigurationVisible = false;
  isDailyCommitment = false;
  isFleetLoaded = false;
  isLoadingCommitments = false;
  isLoadingConfiguration = false;
  isLoadingDetails = false;
  isRecordingAvailable = false;
  isRecordingPlayerVisible = false;
  isRecordingsLoaded = false;
  isSidebarVisible = false;
  isTractorLoaded = false;
  isViewed = false;
  lastestRecordings: RecordingsDto[] = [];
  leadDescription: string;
  leadInformation: CommitmentStatus[];
  leadSubjectTitle: string;
  newCommitment: SelectItem;
  recordings: MenuItem[];
  selectedAudio: string;
  tractor: Tractor;
  unsavedCommitment1: CommitmentChoiceDto;
  unsavedCommitment2: CommitmentChoiceDto;
  leadQuestionInformation: DayDetail;
  isNotFromPSE = true;

  private currentUser: User;

  constructor(
    private authService: AuthService,
    private messageService: MessageService,
    private serviceFactory: ServiceFactory,
    private wigSummaryService: WigSummaryService,
    private route: ActivatedRoute
  ) {
    this.driverWigService = this.serviceFactory.getDriverWigService();
    this.bonusQualificationService = this.serviceFactory.getBonusQualificationService();
    this.driverVOTService = this.serviceFactory.getDriverVOTService();
    this.commitmentDetails = [];
    this.configurationDescription = '';
    this.configurationHeader = '';
    this.leadDescription = '';
    this.leadSubjectTitle = '';
    this.leadQuestionInformation = null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  callRefreshStats() {
    this.refreshStats.emit('From summary component');
  }

  refreshStatsCall() {
    this.callRefreshStats();
  }

  addMessage(message: string): void {
    if (message.includes('Success')) {
      this.messageService.add({
        key: 'tl',
        severity: 'success',
        summary: 'Success',
        detail: message,
      });
    } else {
      this.messageService.add({ key: 'tl', severity: 'error', summary: 'Error', detail: message });
    }
  }

  clearSidebar(): void {
    this.hideSidebar();
    this.isCommitmentFormVisible = false;
    this.commitmentDetails = [];
    this.unsavedCommitment1 = null;
    this.unsavedCommitment2 = null;
    this.newCommitment = null;
  }

  closeRecordingPlayerDialog(): void {
    this.isRecordingPlayerVisible = false;
  }

  creditCall(fleetWeeklyId: number, driverWeeklyId: number): void {
    this.driverWigService
      .creditEligibleCall(this.currentUser.username, fleetWeeklyId, driverWeeklyId)
      .catch((error) => {
        console.log(error);
      });
  }

  daily5Completed(): void {
    const daily5 = this.leadInformation.find((x) => x.leadSubject === 'Daily 5');
    daily5.completionStatus = true;
    this.isSidebarVisible = false;
  }

  dailyCommitmentCompleted(): void {
    const commitment = this.leadInformation.find((x) => x.leadSubject === this.leadSubjectTitle);
    commitment.completionStatus = true;
  }

  displayCommitmentConfigurationForm(): void {
    this.isConfigurationVisible = true;
    this.isLoadingConfiguration = true;

    this.getConfigurationText();

    this.driverWigService
      .getCommitmentConfiguration(this.authService.getCurrentUser().username)
      .then((data) => {
        this.configurationDetails = data.map((x) => new DriverCommitmentConfiguration(x));
      })
      .finally(() => {
        this.isLoadingConfiguration = false;
      });
  }

  displayCommitmentForm(commitment: CommitmentStatus): void {
    this.isDailyCommitment = false;

    if (!commitment.completionStatus) {
      if (commitment.leadSubject.toLowerCase() === this.daily5Commitment.toLowerCase()) {
        this.displayDailyCommitmentForm(commitment);
      } else {
        this.isDailyCommitment = true;
        this.displayDailyCommitmentForm(commitment);
      }
    }
  }

  displayDaily5Commitment(leadSubject: string) {
    this.isCommitmentFormVisible = true;
    this.leadSubjectTitle = leadSubject;
    this.isLoadingDetails = false;
    this.isSidebarVisible = true;
  }

  displayDailyCommitmentForm(commitment: CommitmentStatus): void {
    this.isCommitmentFormVisible = true;
    this.isLoadingDetails = true;
    this.isSidebarVisible = true;
    this.leadSubjectTitle = commitment.leadSubject;
    this.leadDescription = commitment.leadDescription;
    let lookBackDays = -7;
    const date = new Date();
    date.setDate(date.getDate() - 5);
    date.setHours(0, 0, 0, 0);
    if (
      commitment.leadSubject === 'DG Commitments?' ||
      commitment.leadSubject === 'DT_FD Commitments?' ||
      commitment.leadSubject === 'Dollar Weather App?'
    ) {
      lookBackDays = -5;
    }

    this.bonusQualificationService
      .getCommitmentDetailsByLeadId(this.currentUser.username, commitment.leadId)
      .then((data: IDayDetail[]) => {
        this.commitmentDetails = data.map((x) => new DayDetail(x));
      })
      .finally(() => {
        if (this.commitmentDetails && this.commitmentDetails.length > 0) {
          this.leadQuestionInformation = this.commitmentDetails[0];
        }

        this.isLoadingDetails = false;
      });
  }

  getCommitmentChoices(leadNumber: number): void {
    this.driverWigService
      .getCommitmentChoices(this.currentUser.username)
      .then((data) => {
        this.commitmentChoices = data;
        const availableChoices = this.commitmentChoices
          .filter((commitment) => commitment.leadNumber === leadNumber)
          .filter(
            (value, index, commitments) =>
              commitments.findIndex((commitment) => commitment.leadId === value.leadId) === index
          );
        this.currentCommitmentMenuChoices = availableChoices.map(
          (p) =>
            ({
              label: p.leadSubject,
              value: p,
            } as SelectItem)
        );
      })
      .finally(() => {
        this.isCommitmentMenuVisible = true;
      });
  }

  getConfigurationText(): void {
    this.wigSummaryService.getBonusText('Commitment_Engagement').then((data) => {
      this.configurationDescription = data[0].description;
      this.configurationHeader = data[0].heading;
    });
  }

  checkQueryParameter(): void {
    const sourceParam = this.route.snapshot.queryParams.source;
    // const sourcePage = document.referrer;
    if (sourceParam && sourceParam.trim().toUpperCase() === 'PSE') {
      this.isNotFromPSE = false;
      // Additional logic based on 'Source' parameter can go here
    } else {
      this.isNotFromPSE = true;
    }
  }

  getLeadInformation(): void {
    this.isLoadingCommitments = true;
    this.driverWigService
      .getCommitmentStatus(this.currentUser.username)
      .then((data) => {
        if (data) {
          this.leadInformation = data.map((x) => new CommitmentStatus(x));
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        this.isLoadingCommitments = false;
      });
  }

  hideCommitmentMenu(): void {
    this.isCommitmentMenuVisible = false;
  }

  hideSidebar(): void {
    this.isSidebarVisible = false;
    this.isConfigurationVisible = false;
  }

  ngOnInit(): void {
    this.currentUser = this.authService.getCurrentUser();
    this.isViewed = this.authService.isViewed();
    this.isFleetLoaded = false;
    this.isRecordingsLoaded = false;
    this.isTractorLoaded = false;
    this.driverWigService
      .getLatestRecordings(this.currentUser.username)
      .then((data) => {
        this.lastestRecordings = data;
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        this.isRecordingsLoaded = true;
        const recordings = this.lastestRecordings.filter((item) => item.hasRecordingUrl === true);
        if (recordings.length > 0) {
          this.isRecordingAvailable = true;
          this.recordings = recordings.map(
            (recording) =>
              ({
                label: this.transformDate(recording.weekStart),
                icon:
                  recording.hasCalledIn === false
                    ? 'pi pi-caret-right redCircle'
                    : 'pi pi-check grayCircle',
                command: () => {
                  this.openRecordingPlayerDialog(
                    recording.conferenceRecordingUrl,
                    recording.driverWeeklyId
                  );
                },
              } as MenuItem)
          );
        }
      });

    this.driverWigService
      .getFleetWeekly(this.currentUser.username)
      .then((data) => {
        this.fleetWeekly = data;
        this.fleet.emit(data !== null ? data.fleet : '');
        this.driverType.emit(data !== null ? data.type : '');
      })
      .finally(() => {
        this.isFleetLoaded = true;
      });

    this.driverVOTService
      .getTractorsByDriverId(this.currentUser.username)
      .then((data) => {
        this.tractor = data.find(
          (x) => x.drivers.find((y) => y.driver === this.currentUser.username) !== null
        );
      })
      .finally(() => {
        this.isTractorLoaded = true;
      });
    this.checkQueryParameter();
    this.getLeadInformation();
  }

  openRecordingPlayerDialog(conferenceUrl: string, driverWeeklyId: number): void {
    this.recordingPlayer.nativeElement.src = conferenceUrl;
    this.isRecordingPlayerVisible = true;
    this.trackConferenceParticipation(driverWeeklyId);
  }

  saveCommitments(): void {
    let lead1: number;
    if (this.unsavedCommitment1 === undefined || this.unsavedCommitment1 === null) {
      lead1 = this.configurationDetails.find((x) => x.leadNumber === 1).leadId;
    } else {
      lead1 = this.unsavedCommitment1.leadId;
    }

    let lead2: number;
    if (this.unsavedCommitment2 === undefined || this.unsavedCommitment2 === null) {
      lead2 = this.configurationDetails.find((x) => x.leadNumber === 2).leadId;
    } else {
      lead2 = this.unsavedCommitment2.leadId;
    }

    this.driverWigService
      .postCommitmentChoice(this.currentUser.username, lead1, lead2)
      .then((data) => {
        this.addMessage(data);
      })
      .catch((error) => {
        this.addMessage(error.error.text);
      })
      .finally(() => {
        this.clearSidebar();
      });
  }

  setUnsavedCommitment(): void {
    if (this.newCommitment.value.leadNumber === 1) {
      this.unsavedCommitment1 = this.newCommitment.value;
    } else if (this.newCommitment.value.leadNumber === 2) {
      this.unsavedCommitment2 = this.newCommitment.value;
    }
    this.hideCommitmentMenu();
  }

  showCommitmentMenu(leadNumber: number, canChoose: boolean): void {
    if (canChoose) {
      this.getCommitmentChoices(leadNumber);
    }
  }

  showUnsavedChanges(leadNumber: number): boolean {
    if (leadNumber === 1) {
      if (this.unsavedCommitment1 == null) {
        return true;
      }
    } else {
      if (this.unsavedCommitment2 == null) {
        return true;
      }
    }
    return false;
  }

  startRecordingPlayer(): void {
    this.recordingPlayer.nativeElement.play();
  }

  stopRecordingPlayer(): void {
    this.recordingPlayer.nativeElement.pause();
    this.recordingPlayer.nativeElement.currentTime = 0;
  }

  trackConferenceParticipation(driverWeeklyId: number): void {
    this.driverWigService
      .creditEligibleRecording(this.currentUser.username, driverWeeklyId)
      .catch((error) => {
        console.log(error);
      });
  }

  private transformDate(date): string {
    const transformDate = new Date(date);
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    return `Week of ${monthNames[transformDate.getMonth()]} ${transformDate.getDate()}`;
  }
}
